import React, { useEffect } from 'react';
import { useParams, useLocation } from '@thd-olt-component-react/router';
import { extend } from '@thd-nucleus/data-sources';
import { LocalCityRedirector } from '@thd-nucleus/app-render';
import { Typography } from '@one-thd/sui-atomic-components';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { LocalCityPageContent, LocalCityPageFAQ, PromoPod } from '@thd-olt-component-react/emt-generated-content';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { LocalCityMetadata } from '@thd-olt-component-react/metadata';
import { ShopByCategory } from '@thd-olt-component-react/shop-by-category';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import { ProjectsCompleted } from '@thd-olt-component-react/projects-completed';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';
import { Capabilities } from '../../shared-components/Capabilities';
import { convertToTitleCase } from './localCityUtils'
import { LocalCityStorePod } from './LocalCityStorePod';

export const LocalCity = () => {
  const { state, city, guid } = useParams();
  const path = useLocation();

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.local-city-page-load', {
      state,
      city: convertToTitleCase(city)
    });
  }, [state, city]);

  const formatCity = convertToTitleCase(city);
  const formatState = state.toUpperCase();

  return (
    <>
      <ErrorBoundary name="local-city-redirector">
        <LocalCityRedirector guid={guid} />
      </ErrorBoundary>

      <ErrorBoundary name="local-city-metadata">
        <LocalCityMetadata city={formatCity} state={formatState} guid={guid} />
      </ErrorBoundary>

      <div className="sui-max-w-screen-2xl sui-m-auto">
        <div className="sui-flex sui-flex-col sui-px-2 2xl:sui-px-0">
          <ErrorBoundary name="local-city-breadcrumbs">
            <StoreFinderBreadcrumbs
              includeDirectory
              pageName={formatCity}
            />
          </ErrorBoundary>

          <section className="sui-flex sui-flex-col sui-gap-y-8 sui-mt-8">
            <Typography variant="h1" weight="display" height="snug">
              Home Improvement at The Home Depot in {formatCity},&nbsp;{formatState}
            </Typography>

            <ErrorBoundary name="local-city-promo-pod">
              <PromoPod contentId={guid} pageName="lcp" />
            </ErrorBoundary>

            <ErrorBoundary name="local-city-nearby-stores">
              <LocalCityStorePod />
            </ErrorBoundary>

            <ErrorBoundary name="local-city-page-content">
              <LocalCityPageContent guid={guid} />
            </ErrorBoundary>

            <ErrorBoundary name="local-city-page-faq">
              <LocalCityPageFAQ guid={guid} />
            </ErrorBoundary>

            <ErrorBoundary name="local-city-shop-by-category">
              <ShopByCategory componentId="6dMXpZOWCDgD4ZzahLfBpJ" componentClass="ShopByCategory" />
            </ErrorBoundary>

            <ErrorBoundary name="local-city-projects-completed">
              <ProjectsCompleted city={city} state={formatState} />
            </ErrorBoundary>

            <ErrorBoundary name="local-city-capabilities">
              <Capabilities city={city} state={state} />
            </ErrorBoundary>

            <ErrorBoundary name="lcp-popular-categories">
              <PopularCategories
                contentId={guid}
                pageType="lcp"
                headerTitle={`Popular Categories in ${formatCity}, ${formatState}`}
              />
            </ErrorBoundary>

            <ErrorBoundary name="local-city-explore-more">
              <EmtLinks path={path.pathname} />
            </ErrorBoundary>
          </section>
        </div>
      </div>
    </>
  );
}

LocalCity.displayName = 'LocalCity';
LocalCity.propTypes = {};
LocalCity.dataModel = extend(
  LocalCityRedirector,
  Breadcrumbs,
  LocalCityMetadata,
  LocalCityStorePod,
  PromoPod,
  ShopByCategory,
  EmtLinks,
  LocalCityPageContent,
  LocalCityPageFAQ,
  PopularCategories,
  ProjectsCompleted,
);
